import { deepCopy } from "../translation/v0_to_legacy";
import { DISTRO_RULES_BY_KIND, DISTRO_RULES } from "./distroRules";
import { getComponentIdentifier } from "../translation/componentIdentifier";

export type ValidationResult = {
  name: string,
  /** The description of the rule and tips for fixing it. Can be verbose. */
  description: string,
  severity: 'error' | 'warning' | 'info',
  /** A copy of the distro component that failed the rule. */
  component: Object,
  breadcrumbs: string[],
  result: {
    passed: boolean, 
    /** A short message for a failure,
     * including identifiers that help differentiate the failure from similar ones. */
    message?: string
  }
}

/**
 * Validate a distro component and all of its children and return a list of failures.
 * @param component - The distro component to validate.
 * @returns A list of failures.
 */
export const validateDistro = (component: Readonly<Object>, options?: {
  customRules?: typeof DISTRO_RULES_BY_KIND,
}): ValidationResult[] => {
  const rulesByKind = options?.customRules ?? DISTRO_RULES_BY_KIND;
  const results: ValidationResult[] = [];
  const state: Record<typeof DISTRO_RULES[number]['name'], Record<string, any>> = {};
  const stack: Readonly<{component: Object, breadcrumbs: string[]}>[] = [{component, breadcrumbs: []}];

  while (stack.length > 0) {
    const current = stack.pop()!;
    const {component, breadcrumbs} = current;

    if (Array.isArray(component)) {
      // Push array items to stack in reverse order to maintain processing order
      for (let i = component.length - 1; i >= 0; i--) {
        stack.push({component: component[i], breadcrumbs});
      }
    } else if (typeof component === 'object' && component !== null) {
      let newBreadcrumbs = breadcrumbs;
      if ('kind' in component && typeof component.kind === 'string') {
        newBreadcrumbs = [...breadcrumbs, `${component.kind}: ${getComponentIdentifier(component)}`];
        rulesByKind[component.kind]?.forEach(rule => {
          state[rule.name] ??= {};
          // Remove _trackedObjInfo (and anything else not needed for validating the survey) from the fn call
          const componentWithoutTrackedObjInfo = deepCopy(component);
          delete componentWithoutTrackedObjInfo['_trackedObjInfo'];
          try {
            const result = rule.fn(componentWithoutTrackedObjInfo, state[rule.name]);

            // Always add the result to the results array, and filter to failures later,
            // since rules might modify the result in place in future loop iterations
            results.push({
              name: rule.name,
              description: rule.description,
              severity: rule.severity,
              component: componentWithoutTrackedObjInfo,
              breadcrumbs: newBreadcrumbs,
              result
            });
          } catch (e) {
            console.error(`Error running rule ${rule.name} on component ${getComponentIdentifier(componentWithoutTrackedObjInfo)}`, e);
            results.push({
              name: rule.name,
              description: rule.description,
              severity: rule.severity,
              component: componentWithoutTrackedObjInfo,
              breadcrumbs: newBreadcrumbs,
              result: { passed: false, message: 'Error running rule' }
            });
          }
          
        });
      }
      // Push object property values to stack in reverse order to maintain processing order
      const entries = Object.entries(component);
      for (let i = entries.length - 1; i >= 0; i--) {
        stack.push({component: entries[i][1], breadcrumbs: newBreadcrumbs});
      }
    }
  }

  return results.filter(r => !r.result.passed);
}
